import type { ApplicationTargetResolution } from './applicationTargetResolver'
import { normalizeApplicationInfrastructureType } from './applicationTargetResolver'

export type ApplicationDuplicateTarget = {
	infrastructureType: 'gke' | 'non-gke'
	platformID?: string
	platformNameValue?: string
	gcpProjectID?: string
	projectId?: string
}

const isRecord = (value: unknown): value is Record<string, any> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getString = (value: unknown): string => {
	if (isRecord(value)) {
		return String(value.value ?? value.label ?? '').trim()
	}

	return String(value ?? '').trim()
}

const getValueString = (value: unknown): string => {
	if (isRecord(value)) {
		return String(value.value ?? '').trim()
	}

	return String(value ?? '').trim()
}

const normalize = (value: unknown): string =>
	getString(value).trim().toLowerCase()

const normalizeValue = (value: unknown): string =>
	getValueString(value).trim().toLowerCase()

const firstPresent = (...values: unknown[]) =>
	values.map(getString).find(Boolean) || ''

const hasAnyIdentity = (values: string[], identities: string[]) =>
	values.some((value) => identities.includes(value))

export const getRandomTwoDigitSuffix = (random = Math.random) =>
	String(Math.floor(random() * 100)).padStart(2, '0')

const matchesExactOrFinalPathSegment = (value: string, expected: string) => {
	if (!value || !expected) {
		return false
	}

	if (value === expected) {
		return true
	}

	const finalSegment = value.split('/').filter(Boolean).pop()
	return finalSegment === expected
}

export const buildDuplicateTargetFromResolution = (
	target: ApplicationTargetResolution,
	infrastructureTypeSelection?: unknown
): ApplicationDuplicateTarget => {
	const infrastructureType = normalizeApplicationInfrastructureType(
		infrastructureTypeSelection
	)

	if (infrastructureType === 'gke') {
		const clusterID = getString(target.resolvedCluster?.platformID)

		return {
			infrastructureType: 'gke',
			platformID: clusterID,
			platformNameValue: clusterID
		}
	}

	const projectUUID = getString(target.selectedProject?.gcpProjectID)
	const projectName = firstPresent(
		target.selectedProject?.gcpProjectName,
		target.selectedProject?.details?.gcpProjectName,
		target.selectedProject?.details?.projectName
	)
	const projectId = firstPresent(
		target.selectedProject?.projectId,
		target.selectedProject?.details?.projectId,
		projectName,
		projectUUID
	)

	return {
		infrastructureType: 'non-gke',
		platformID: projectUUID,
		gcpProjectID: projectName || projectUUID,
		projectId
	}
}

export const applicationMatchesDuplicateTarget = (
	application: any,
	target: ApplicationDuplicateTarget
): boolean => {
	const details = application?.details || {}

	if (target.infrastructureType === 'gke') {
		const clusterID = normalize(target.platformID || target.platformNameValue)
		const platformID = normalize(details.platformID)
		const platformNameValue = normalizeValue(details.platformName)

		return Boolean(
			clusterID &&
				(platformID === clusterID ||
					matchesExactOrFinalPathSegment(platformNameValue, clusterID))
		)
	}

	const targetIdentities = [
		normalize(target.platformID),
		normalize(target.gcpProjectID),
		normalize(target.projectId)
	].filter(Boolean)
	const applicationIdentities = [
		normalize(details.platformID),
		normalize(details.gcpProjectID),
		normalize(details.projectId)
	].filter(Boolean)

	return hasAnyIdentity(applicationIdentities, targetIdentities)
}

export const getExistingEphemeralNumbersForTarget = (
	applications: any[] | undefined,
	applicationName: string,
	target: ApplicationDuplicateTarget
) => {
	if (!applications) {
		return []
	}

	return applications.reduce<string[]>((suffixes, app) => {
		const details = app?.details

		if (
			details?.applicationName === applicationName &&
			applicationMatchesDuplicateTarget(app, target) &&
			details.additionalEnvName &&
			/\d{2}$/.test(details.additionalEnvName)
		) {
			suffixes.push(details.additionalEnvName.slice(-2))
		}

		return suffixes
	}, [])
}
